/* eslint-disable no-console */
import {
  faCheck,
  faChevronLeft,
  faChevronRight,
  faMapMarkerAlt,
  faTimes
} from "@awesome.me/kit-cae4660dd3/icons/classic/regular";
import { faChevronDown, faChevronUp } from "@awesome.me/kit-cae4660dd3/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EducatorContentSpinner from "../educator-content-spinner";
import ContentReader, { IPath, ISegment, ISegmentName } from "@imaginelearning/mps-ctk-reader-wrapper";
import React, { useEffect, useRef, useState } from "react";
import { Button, Carousel, Dropdown, Row } from "react-bootstrap";
import {
  IActivity,
  IActivitySegment,
  IActivitySegmentsState,
  IEducatorReviewerProps,
  Languages,
  ServiceStatusEnum,
  defaultDoolittleVersion
} from "../educator-content-reader.props";
import { removeAllCustomArrayMethods } from "../../../helpers/removeAllCustomArrayMethods";
import "../educator-content-reader.scss";
import "./educator-reviewer.scss";
import { LanguageEnum } from "../../../wne-mpng-components-mf";

declare global {
  interface Window {
    ContentReader: any;
  }
}
window.ContentReader = window.ContentReader || {};

const EducatorReviewer = ({
  doolittleBaseMediaUrl,
  doolittleBaseApiUrl,
  activities,
  selectedIndex,
  setMinHeight,
  lessonAttemptLanguage,
  getActivityToken,
  itemEngineAuthorUri,
  itemEngineItemsUri
}: IEducatorReviewerProps) => {
  const [selectedActivity, setSelectedActivity] = useState({
    ...activities[selectedIndex || 0],
    index: selectedIndex || 0
  });
  const [selectedActivitySegment, setSelectedActivitySegment] = useState<IActivitySegment>();
  const [activitySegments, setActivitySegments] = useState<IActivitySegmentsState>({ response: [] });
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [carouselItemCount, setCarouselItemCount] = useState(14);
  const [currentSegment, setCurrentSegment] = useState<ISegment | null>(null);
  const [segmentTitles, setSegmentTitles] = useState<ISegmentName[] | null>(null);
  const [sectionState, setSectionState] = useState({
    status: ServiceStatusEnum.LOADING,
    payload: "",
    showPlayer: false
  });

  const [down, setDown] = React.useState(true);
  const handleToggleClick = () => {
    setDown(!down);
  };
  const handleChevronIcon = () => {
    if (down) {
      return <FontAwesomeIcon icon={faChevronDown} />;
    } else {
      return <FontAwesomeIcon icon={faChevronUp} />;
    }
  };

  const carouselDivRef = useRef<HTMLDivElement>(null);

  const handleActivitySelect = (key: string | null) => {
    setCarouselIndex(0);
    const index = parseInt(key as string, 10);
    if (index > -1) {
      if (index !== selectedActivity.index) {
        window.ContentReader.destroy();
      }
      setSelectedActivity({ ...activities[index], index });
    }
  };

  const handleSegmentSelect = (item: any) => {
    setSelectedActivitySegment(item);
    window.ContentReader.goToSegment(item.segmentID);
  };

  window.addEventListener("resize", () => {
    setCarouselIndex(0);
    handleCarouselItemCalc();
  });

  const handleCarouselItemCalc = () => {
    if (carouselDivRef.current) {
      const carousel = carouselDivRef.current;
      // Subtracting 40 to account for carousel arrows
      const clientWidth = carousel.clientWidth - 40;
      const nmbOfItemsPerSlide = Math.ceil(clientWidth / 86);
      setCarouselItemCount(nmbOfItemsPerSlide);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const nextSectionTokenResult = await getActivityToken(
        selectedActivity.pathId,
        selectedActivity.pathVersion,
        selectedActivity.takeId || "",
        selectedActivity.isMasteryCheck,
        selectedActivity.lessonAttemptId || ""
      );

      if (nextSectionTokenResult.status === ServiceStatusEnum.LOADED) {
        if (nextSectionTokenResult.payload) {
          const showPlayer = true;
          const { status, payload } = nextSectionTokenResult;
          setSectionState({ status, payload, showPlayer });
        }
      }
    };
    if (selectedActivity.pathId) {
      setSectionState({
        status: ServiceStatusEnum.LOADING,
        payload: "",
        showPlayer: false
      });
      fetchData();
    }
  }, [selectedActivity.pathId, selectedActivity.index, selectedActivity.takeId]);

  useEffect(() => {
    const index = selectedActivity.index;
    setSelectedActivity({ ...activities[index], index });
  }, [activities, selectedActivity.index]);

  useEffect(() => {
    handleCarouselItemCalc();
    //the Assignment Builder MFE uses a module that adds custom methods to the Array prototype.
    //To prevent these custom methods from polluting our IN loops, we remove them here.
    return () => {
      removeAllCustomArrayMethods();
    };
  }, []);

  const renderItems = (items: any, startIndex: number, maxIndex: number) => {
    if (items !== null) {
      const indexCount = maxIndex > items.length ? items.length : maxIndex;

      return items.slice(startIndex, indexCount).map((item: any, index: number) => {
        const currentSegmentItem = selectedActivitySegment?.segmentID === item.segmentID;
        let itemWrapperClass = "item-wrapper";
        itemWrapperClass += currentSegmentItem ? " item-backdrop" : "";
        let itemClass = item.completed ? "complete" : "incomplete";
        itemClass += currentSegmentItem ? " active" : "";
        const iconDisplay = item.inProgress || (item.completed && item.correct != null);

        let itemIcon;
        let itemIconClass = "icon-float ";
        if (item.inProgress) {
          itemIcon = faMapMarkerAlt;
          itemIconClass += "item-inprogress";
        } else if (item.correct) {
          itemIcon = faCheck;
          itemIconClass += "item-correct";
        } else if (!item.correct) {
          itemIcon = faTimes;
          itemIconClass += "item-incorrect";
        }

        return (
          <div key={index} className={itemWrapperClass}>
            <div
              onClick={() => handleSegmentSelect(item)}
              className={`lesson-review-carousel-item font-weight-bold ${itemClass}`}
              role="button"
              tabIndex={0}
            >
              {item.displayOrder}
              {iconDisplay && (
                <FontAwesomeIcon
                  size={"md" as any}
                  icon={itemIcon as any}
                  className={`${itemIconClass} bg-white shape rounded-circle p-1`}
                  style={{
                    width: 20,
                    height: 20
                  }}
                />
              )}
            </div>
          </div>
        );
      });
    }

    return null;
  };

  const renderPanel = (items: any, panelIndex: number) => {
    const itemCount: number = carouselItemCount;
    const startIndex = panelIndex * itemCount - itemCount;
    const maxIndex = panelIndex * itemCount;
    return (
      <div key={panelIndex} className="carousel-item">
        {renderItems(items, startIndex, maxIndex)}
      </div>
    );
  };

  const renderCarouselBody = (items: any) => {
    const itemCount: number = carouselItemCount;
    // Determine how many times to render a carousel panel
    const panels = items != null ? Math.ceil(items.length / itemCount) : 0;
    const renderPanelData = [];
    if (panels !== 0) {
      for (let i = 1; i <= panels; i += 1) {
        renderPanelData.push(renderPanel(items, i));
      }
    }
    return renderPanelData;
  };

  const renderCarousel = () => {
    return (
      <div className="h-auto w-100 mb-2 carousel-container" ref={carouselDivRef}>
        <Carousel
          prevIcon={<FontAwesomeIcon size="2x" icon={faChevronLeft} />}
          nextIcon={<FontAwesomeIcon size="2x" icon={faChevronRight} />}
          interval={null}
          indicators={false}
          className="w-100"
          activeIndex={carouselIndex}
          onSelect={(eventKey: number) => setCarouselIndex(eventKey)}
          wrap={false}
        >
          {renderCarouselBody(activitySegments.response)}
        </Carousel>
      </div>
    );
  };

  const handleSegmentStateChange = (segment: any) => {
    const currentSegmentItem =
      activitySegments.response.find((item: any) => item.segmentID === segment.segmentId) ??
      activitySegments.response[0];
    const activitySegment: IActivitySegment = {
      completed: currentSegmentItem.completed,
      displayOrder: currentSegmentItem.displayOrder,
      inProgress: currentSegmentItem.inProgress,
      segmentID: currentSegmentItem.segmentID,
      correct: currentSegmentItem.correct
    };
    setSelectedActivitySegment(activitySegment);
  };

  const handleTakeStateChange = (take: any) => {
    if (take.components != null) {
      const lastCompleteItem: number = take.components.reverse().find((item: any) => item.state.completed === true);
      let inProgressItem: any = null;
      if (lastCompleteItem !== undefined) {
        const lastItemIndex = take.components.reverse().indexOf(lastCompleteItem);
        inProgressItem = take.components[lastItemIndex + 1];
      }

      setActivitySegments({
        response: take.totalsBySegment.map((segment: any, index: number) => {
          const components = take.components.filter((item: any) => item.segmentId === segment.segmentId);
          const questionComponent =
            components.length > 1
              ? components.find((item: any) => item.type === "Question" || item.type === "QuestionItem")
              : take.components.find((item: any) => item.segmentId === segment.segmentId);
          const completed = questionComponent !== undefined ? questionComponent.state.completed : false;
          const correct = questionComponent !== undefined ? questionComponent.state.correct : false;

          const activitySegment: IActivitySegment = {
            segmentID: segment.segmentId,
            completed,
            correct,
            displayOrder: index + 1,
            inProgress:
              inProgressItem !== null && inProgressItem !== undefined
                ? inProgressItem.segmentId === segment.segmentId
                : false
          };
          return activitySegment;
        })
      });
    }
  };

  const EducatorListener = {
    onPathLaunched: (path: IPath) => {
      if (path) {
        setSegmentTitles(path.segments);
      }
    },
    onSegmentStateChange: (segment: any) => {
      if (JSON.stringify(currentSegment) !== JSON.stringify(segment)) {
        if (segmentTitles) {
          const current = segmentTitles.find((x) => x.id === segment.segmentId);
          if (current) {
            segment.title = current.title;
          }
        }
        setCurrentSegment(segment);
      }
      handleSegmentStateChange(segment);
    },
    onTakeStateChange: (take: any) => {
      handleTakeStateChange(take);
    }
  };

  window.ContentReader = { ...window.ContentReader, ...EducatorListener };

  return (
    <div className="educator-content-reader-review" style={setMinHeight ? { minHeight: "900px" } : {}}>
      <div className="h-auto w-100 mb-2 row-dropdowns">
        <Dropdown
          role="menu"
          className="activity-dropdown"
          onSelect={handleActivitySelect}
          onClick={handleToggleClick}
          drop="down"
        >
          <Dropdown.Toggle id="activity-dropdown">
            {selectedActivity.type} {handleChevronIcon()}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {activities.map((item: IActivity, index: number) => {
              const selectedItem = index === selectedActivity.index;
              return (
                <Dropdown.Item key={index} active={selectedItem} eventKey={index.toString()} disabled={false}>
                  {item.type}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
        <Button disabled variant="outline-ilp" style={{ color: "black", borderColor: "black" }}>
          {lessonAttemptLanguage}
        </Button>
      </div>
      <hr className="hr-small" />
      {renderCarousel()}
      <Row className="h-100 w-100">
        {sectionState.showPlayer && sectionState.status === ServiceStatusEnum.LOADING && <EducatorContentSpinner />}
        {sectionState.showPlayer && sectionState.status === ServiceStatusEnum.LOADED && (
          <ContentReader
            token={sectionState.payload}
            doolittleBaseMediaUrl={doolittleBaseMediaUrl}
            doolittleBaseApiUrl={doolittleBaseApiUrl}
            doolittleVersion={defaultDoolittleVersion}
            displayCorrectAnswer={true}
            shouldAutoPlay={false}
            isSpanishContent={lessonAttemptLanguage === Languages[LanguageEnum.Spanish]}
            itemEngineAuthorUri={itemEngineAuthorUri}
            itemEngineItemsUri={itemEngineItemsUri}
          />
        )}
      </Row>
    </div>
  );
};

export default EducatorReviewer;
